import gsap from 'gsap'
import Animation from 'nanogl-gltf/lib/elements/Animation'
import MaterialOverrideExtension from 'nanogl-gltf/lib/extensions/MaterialOverrideExtension'
import Gltf from 'nanogl-gltf/lib/Gltf'
import UnlitPass from 'nanogl-pbr/UnlitPass'

import { SFXAudio } from '@/config/AudioConfig'
import AudioEngine from '@/webgl/audio/AudioEngine'
import { RenderContext } from '@/webgl/core/RenderContext'
import Signal from '@/webgl/core/Signal'
import AssetDatabase from '@/webgl/resources/AssetDatabase'
import GltfResource from '@/webgl/resources/GltfResource'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import { IRunnerRenderer } from '@/webgl/runner/interfaces/IRunnerRenderer'
import { SmokeChunk } from '@/webgl/runner/rendering/SmokeRenderer'
import { ScenarioSetup } from '@/webgl/runner/scenarios/ScenarioSetup.types'
import { IGameSegment } from '@/webgl/runner/systems/SequencingSystem'

export default class MilkSegment implements IGameSegment {
  onComplete: Signal<void>
  milkReveal: number = 0

  gltf: Gltf
  animation: Animation
  animationDuration: number
  smokeChunk: SmokeChunk
  smokeReveal: number = 0.0
  world: GameWorld
  renderer: IRunnerRenderer
  renderActive: boolean = false
  timeline: gsap.core.Timeline
  tween: gsap.core.Tween

  constructor(readonly id: string) {
    this.smokeChunk = new SmokeChunk()

    // const fld = gui.folder("Milk");
    // fld.add(this.smokeChunk, 'WaveLengh', { min: 0, max: 50 });
    // fld.add(this.smokeChunk, 'WaveOffset', { min: 0, max: 0.2 });
    // fld.add(this.smokeChunk, 'WaveAmplitude', { min: 0, max: 0.3 });
    // fld.add(this.smokeChunk, 'WaveSpeed', { min: 0, max: 20 });
    // fld.add(this, "smokeReveal", { min: 0, max: 1 });

    this.smokeChunk.WaveLengh = 26.0869565217
    this.smokeChunk.WaveOffset = 0.2
    this.smokeChunk.WaveAmplitude = 0.097826087
    this.smokeChunk.WaveSpeed = 8.0
    this.smokeChunk.useReveal()
    this.smokeChunk.setColor(0xfff4e5)
  }

  init(world: GameWorld, renderer: IRunnerRenderer, setup: ScenarioSetup): void {
    this.world = world
    this.renderer = renderer
  }

  async load(): Promise<void> {
    const dirtMilk = await AssetDatabase.getTexture('pedro_1/milk_dirt.png', this.renderer.gl, { alpha: true }).load()
    this.renderer.lanes.scroll.setExtraTex(dirtMilk)

    const overrides: MaterialOverrideExtension = new MaterialOverrideExtension()

    overrides.overridePass('smoke', (ctx, m) => {
      const passInstance = m.getPass('color')
      const pass = passInstance.pass as UnlitPass
      pass.glconfig.enableBlend(true)
      pass.glconfig.blendFunc(this.renderer.gl.SRC_ALPHA, this.renderer.gl.ONE_MINUS_SRC_ALPHA)
      pass.inputs.add(this.smokeChunk)
      return passInstance.pass
    })

    this.gltf = await new GltfResource('pedro_1/milk.glb', this.renderer.gl, { extensions: [overrides] }).load()

    this.animation = this.gltf.animations[0]
    this.animationDuration = this.animation.duration

    this.renderer.world.root.add(this.gltf.root)
  }

  start(): void {
    AudioEngine.play(SFXAudio.Milk)

    this.world.getTrackingSystem().trackImpression({
      unitName: `pedro_simulation_0`,
      unitVariation: `scenario_auto_obstacle_milk`,
      unitLocation: 'tyce',
    })

    this.world.state.alterJauge(1.0)
    this.renderActive = true
    const state = this.world.state
    this.world.player.grabDisabled = true

    const animation = { p: 0 }
    this.tween = gsap.to(animation, {
      p: this.animationDuration,
      duration: this.animationDuration,
      ease: 'none',
      onUpdate: () => {
        this.animation.evaluate(animation.p)
      },
    })

    AudioEngine.play('p1_milk_turningpoint_A')

    this.timeline = gsap
      .timeline({
        onComplete: () => {
          gsap.to(state.value, { jauge: 1, duration: 2.0 })
          this.renderActive = false
          this.world.player.grabDisabled = false
          AudioEngine.play('p1_milk_rand_1', { delay: 0.6 })
        },
      })
      .to(this, { smokeReveal: 0.5, duration: 1.2, delay: 0.7, ease: 'none' })
      .to(this, { smokeReveal: 1.0, duration: 1.5, delay: 1.0, ease: 'none' })
      .to(this, { milkReveal: 1, duration: 1 }, '-=0.8')
  }

  stop() {
    this.timeline?.kill()
    this.tween?.kill()

    this.world.player.grabDisabled = false
    this.smokeReveal = 0
    gsap.to(this, {
      milkReveal: 0,
      duration: 1,
      onUpdate: () => this.renderer.lanes.scroll.setExtraReveal(this.milkReveal),
    })
  }

  update(time: TimeEntity): void {
    this.renderer.lanes.scroll.setExtraReveal(this.milkReveal)
    this.smokeChunk.revealUniform.set(this.smokeReveal)
    this.smokeChunk.timeUniform.set(this.smokeChunk.timeUniform.value[0] + time.deltaTime)

    this.gltf.root.x = this.renderer.world.player.node.x
  }

  render(context: RenderContext): void {
    if (!this.renderActive) return
    for (const renderable of this.gltf.renderables) {
      renderable.render(context.gl, context.camera, context.mask, context.pass, context.glConfig)
    }
  }
}
